import { FlexBox } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import {
  Conversation as ConversationT,
  MetaConversation,
  Tag,
} from 'lib-shared-legacy-types';
import { Sentry } from 'lib-shared-sentry';
import { Conversation as ConversationType, User } from 'lib-shared-types-api';
import _isEqual from 'lodash/isEqual';
import { memo, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import {
  getConversationTitle,
  getHumanConversationChannel,
} from 'app/utils/conversationTitle';
import {
  ConversationContextProvider,
  ConversationStatusColorBox,
  ConversationTimelineDetails,
} from 'components/conversation';
import { getConversation } from 'components/conversation/api/conversation';
import { RestrictedConversation } from 'components/conversation/components/RestrictedConversation';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useServerStorage } from 'utils/useServerStorage';
import { isRestrictedFromQueue, useUserQueues } from 'utils/user/useUserQueues';

import ConversationSidebar from '../containers/conversationSidebar';
import Timeline from '../containers/timeline';

import { ConversationSummary } from './conversationSummary';
import { ConversationUnlinkButton } from './conversationUnlinkButton/conversationUnlinkButton';
import { ConversationHeader } from './header';
import { SubConversationList } from './subConversationList';
import { SubConversationListItem } from './subConversationList/subConversationList';

import '../styles/conversation.less';

export type ConversationProps = {
  metaConversation: MetaConversation;
  latestMessengerConversation: any;
  isCustomCardMenuOpen: boolean;
  conversationUnmount: any;
  conversationResetUnread: (payload: any) => void;
  conversationGet: (payload: any) => Promise<any>;
  notificationTabBadgeReset: () => void;
  isFocusMode: boolean;
  closeSideBarMenu: () => void;
};

const getConversationRequesterDetails = (requester: User | null) =>
  requester?.name || requester?.email || requester?.phone_number || undefined;

function Conversation(props: ConversationProps): JSX.Element | null {
  const flags = useFeatureFlag(['link-merge-conversations']);
  const params = useAppPathParams('/conversation/:csid');
  const csid: number = parseInt(params.csid, 10);
  const storage = useServerStorage('user');

  const isTimelineExpanded = flags.linkMergeConversations
    ? storage.data.conversation?.isTimelineExpanded ?? false
    : false;
  const navigate = useAppNavigate();
  const [isRestricted, setIsRestricted] = useState<boolean | undefined>(
    undefined,
  );

  const {
    metaConversation,
    isCustomCardMenuOpen,
    isFocusMode,
    conversationResetUnread,
    conversationGet,
    notificationTabBadgeReset,
    conversationUnmount,
    closeSideBarMenu,
  } = props;
  const {
    parentConversation,
    parentConversationRequester,
    linkedConversations,
    conversation,
    requester,
  } = metaConversation;

  const userQueues = useUserQueues();

  const userQueuesAsyncStatus = userQueues.asyncStatus;

  /**
   * Before initializing the conversation we need to determine if the user is
   * restricted from the conversation. Therefore, we first fetch the
   * conversation and check if the user is restricted. If they are not
   * restricted we go ahead otherwise we skip the initialization and enable the
   * restricted state.
   */
  useEffect(() => {
    if (userQueuesAsyncStatus === 'fulfilled') {
      getConversation({ csid }).then((res) => {
        const queue = res.data.history.main_conversation.queue;
        const restricted = isRestrictedFromQueue(queue, userQueues);

        if (!restricted) {
          conversationGet({ csid, prefetchedResult: res }).catch(
            Sentry.captureException,
          );
          conversationResetUnread({ csid });
          notificationTabBadgeReset();
        }
        setIsRestricted(restricted);
      });
    }

    return () => conversationUnmount();
    // We listen for csid and the loading state of userQueues
    // to be able to check if the user is restricted from the queue.
    // We don't perform any actions until we have the required data loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csid, userQueuesAsyncStatus]);

  const toggleTimeline = () =>
    storage.set({
      conversation: (draft) => {
        draft.isTimelineExpanded = !draft.isTimelineExpanded;
      },
    });

  if (isRestricted === undefined) {
    return null;
  }

  /**
   * Related link type is considered to be a same level link (as opposed to
   * parent-child relation). And should allow to display a timeline.
   */
  const isRelatedConversation = conversation.link_type === 'related';

  return isRestricted ? (
    <RestrictedConversation />
  ) : (
    <ConversationContextProvider conversation={metaConversation.conversation}>
      <div className="conversation-view">
        {(!parentConversation || isRelatedConversation) && (
          <div
            className={classnames('conversation-view__timeline', {
              'conversation-view__timeline--placeholder': !!parentConversation,
              'conversation-view__focus-mode': isFocusMode,
              'conversation-view__timeline--details': isTimelineExpanded,
            })}
          >
            {isTimelineExpanded ? (
              <ConversationTimelineDetails
                csid={conversation.id as ConversationType['id']}
                requesterId={requester?.id as string}
                handleToggleTimeline={toggleTimeline}
              />
            ) : (
              <Timeline handleToggleTimeline={toggleTimeline} />
            )}
          </div>
        )}
        <div className="conversation-view__main">
          {!!parentConversation && (
            <div
              className={classnames('conversation-view__parent', {
                'conversation-view__parent__related': isRelatedConversation,
              })}
              onClick={() =>
                navigate({
                  path: '/conversation/:csid',
                  params: {
                    path: {
                      csid: `${parentConversation.id}`,
                    },
                  },
                })
              }
              data-status={parentConversation.status}
            >
              <ConversationStatusColorBox
                className="conversation-status"
                csid={parentConversation.id as ConversationType['id']}
                status={parentConversation.status as ConversationType['status']}
              />

              <ConversationSummary
                className="conversation-view__parent__summary"
                conversationAttributes={
                  metaConversation.conversation.attributes
                }
                conversationInfo={parentConversation}
                labels={{
                  title: getConversationTitle({
                    conversation: parentConversation,
                    requester: parentConversationRequester,
                  }),
                  agent: conversation.assignment?.agent?.name || undefined,
                  requester: getConversationRequesterDetails(
                    parentConversationRequester || null,
                  ),
                  queue: parentConversation.queue?.name,
                  channel: getHumanConversationChannel(
                    parentConversation,
                  ) as any,
                }}
              />
              {flags.linkMergeConversations && (
                <FlexBox
                  flex="1"
                  justifyContent="flex-end"
                  alignItems="center"
                  margin="0 200"
                >
                  <ConversationUnlinkButton />
                </FlexBox>
              )}
            </div>
          )}
          <div
            data-status={metaConversation.conversation.status}
            className="conversation-view__current conversation-messages"
          >
            <ConversationHeader />
            <Outlet />
          </div>
          {!parentConversation &&
            linkedConversations &&
            linkedConversations.length > 0 && (
              <div className="conversation-view__children">
                <SubConversationList
                  conversations={linkedConversations.map<SubConversationListItem>(
                    (metaC: {
                      conversation: ConversationT;
                      notes: Array<any>;
                      requester: User;
                      tags: Array<Tag>;
                    }) => {
                      return {
                        info: metaC.conversation,
                        requester: metaC.requester,
                        labels: {
                          title: getConversationTitle(metaC),
                          agent: metaC.conversation.assignment?.agent?.name,
                          queue: metaC.conversation.queue?.name,
                          channel: getHumanConversationChannel(
                            metaC.conversation,
                          ),
                        },
                      } as SubConversationListItem;
                    },
                  )}
                  onConversationClick={(csid: number | string) =>
                    navigate({
                      path: '/conversation/:csid',
                      params: {
                        path: {
                          csid: `${csid}`,
                        },
                      },
                    })
                  }
                />
              </div>
            )}
        </div>
        <div
          className={classnames('conversation-view__sidebar', {
            closed: !isCustomCardMenuOpen,
          })}
        >
          <ConversationSidebar />
        </div>

        <div
          className={classnames('conversation-view__backdrop', {
            closed: !isCustomCardMenuOpen,
          })}
          onClick={closeSideBarMenu}
        />
      </div>
    </ConversationContextProvider>
  );
}

export default memo(Conversation, _isEqual);
